import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Mail, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatusColor = (status) => {
  switch (status) {
    case "pending": case "waiting": return "bg-terracotta";
    case "confirmed": case "contacted": case "in_progress": return "bg-olive";
    case "completed": case "enrolled": return "bg-charcoal";
    case "cancelled": case "removed": return "bg-red-200 text-red-800";
    default: return "bg-sand text-charcoal";
  }
};

export const BreedersTab = ({
  breederReferrals,
  getAuthHeaders,
  fetchAllData
}) => {
  const handleSendBreederInvitation = async (referralId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/breeder-referrals/${referralId}/send-invitation`, {}, { headers });
      toast.success("Invitation sent successfully!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to send invitation");
    }
  };

  const handleDeleteBreederReferral = async (id, ownerName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/breeder-referrals/${id}`, { headers });
      toast.success(`Referral for ${ownerName} deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete referral");
    }
  };

  return (
    <div className="card-premium p-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-sand/50">
            <TableHead>Breeder</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Puppy</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {breederReferrals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-stone">
                No breeder referrals yet
              </TableCell>
            </TableRow>
          ) : (
            breederReferrals.map((referral) => (
              <TableRow key={referral.id}>
                <TableCell>
                  <div className="font-medium">{referral.breeder_name}</div>
                  <div className="text-xs text-stone">{referral.breeder_email}</div>
                </TableCell>
                <TableCell>
                  <div>{referral.owner_name}</div>
                  <div className="text-xs text-stone">{referral.owner_email}</div>
                </TableCell>
                <TableCell>
                  <div>{referral.puppy_breed}</div>
                  <div className="text-xs text-stone">DOB: {referral.puppy_dob}</div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(referral.status)}>{referral.status}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {!referral.invitation_sent && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendBreederInvitation(referral.id)}
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Send Invite
                      </Button>
                    )}
                    {referral.invitation_sent && (
                      <span className="text-xs text-olive">Invitation sent</span>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Breeder Referral</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete the referral for <strong>{referral.owner_name}</strong> from <strong>{referral.breeder_name}</strong>?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => handleDeleteBreederReferral(referral.id, referral.owner_name)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
